import * as React from 'react';
import {
    StyleSheet,
    type TextProps,
    type TextStyle,
    // @ts-ignore
    unstable_createElement,
    type ViewProps,
} from 'react-native';

export type DOMViewTag = keyof HTMLElementTagNameMap;

/**
 * View with a native DIV that can be accessed by a ref.
 */
export const DOMView = React.forwardRef(function DOMViewWithRef<Tag extends DOMViewTag = 'div'>(
    props: Omit<React.DOMAttributes<HTMLElementTagNameMap[Tag]>, keyof ViewProps> &
        ViewProps & {
            children?: React.ReactNode;
            editable?: boolean;
            spellCheck?: boolean;
            tag?: Tag;
            translate?: boolean;
        },
    ref: React.Ref<HTMLElement>
) {
    const {
        editable,
        tag = 'div',
        style,
        // props to filter out
        onResponderGrant,
        onResponderRelease,
        // @ts-ignore
        onPressIn,
        // @ts-ignore
        onPressOut,
        onStartShouldSetResponder,
        onMoveShouldSetResponder,
        onStartShouldSetResponderCapture,
        onMoveShouldSetResponderCapture,
        onResponderReject,
        onResponderStart,
        onResponderMove,
        onResponderEnd,
        onResponderTerminate,
        onResponderTerminationRequest,
        translate,
        ...rest
    } = props;

    return unstable_createElement(tag, {
        ...rest,
        ref,
        contentEditable: editable,
        style: [isInlineTag(tag) ? null : styles.domDivView, style],
        ...(typeof translate === 'boolean' ? { translate: translate ? 'yes' : 'no' } : {}),
    });
});

/**
 * Text with a native span where DOM attributes can be passed.
 */
export const DOMText = React.forwardRef(function DOMTextWithRef<Tag extends DOMViewTag = 'span'>(
    props: Omit<React.DOMAttributes<HTMLElementTagNameMap[Tag]>, keyof TextProps> &
        TextProps & {
            children?: React.ReactNode;
            editable?: boolean;
            selectable?: boolean;
            spellCheck?: boolean;
            tag?: Tag;
        },
    ref: React.Ref<HTMLElement>
) {
    const { editable, selectable, tag = 'span', style, ...rest } = props;

    return unstable_createElement(tag, {
        ...rest,
        ref,
        contentEditable: editable,
        style: [selectable && styles.domTextSelectable, styles.domText, style],
    });
});

function isInlineTag(tag: DOMViewTag): boolean {
    return ['span', 'strong', 'code', 'em', 'mark'].includes(tag);
}

/**
 * Render a view that is not content editable.
 */
export function NonEditableView(
    props: ViewProps & {
        children: React.ReactNode;
        tag?: DOMViewTag;
    }
): React.ReactElement {
    return <DOMView {...props} editable={false} />;
}

const styles = StyleSheet.create({
    domDivView: {
        // Same style as the default one for views:
        // https://github.com/necolas/react-native-web/blob/master/packages/react-native-web/src/exports/View/index.js#L146
        alignItems: 'stretch',
        borderStyle: 'solid',
        borderColor: 'black',
        borderWidth: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexBasis: 'auto',
        flexDirection: 'column',
        flexShrink: 0,
        margin: 0,
        minHeight: 0,
        minWidth: 0,
        padding: 0,
        position: 'relative',
        zIndex: 0,
    },
    domText: {
        // reset margin for text so we can use DomText with headings
        margin: 0,
    },
    domTextSelectable: {
        userSelect: 'text',
    } as TextStyle,
});
